import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { mockData } from '../mock';

const DoorAutomationSubsections = () => {
  const { subsection } = useParams();
  const navigate = useNavigate();
  
  const sectorData = mockData.sectors.doorAutomation;
  const subsectionData = sectorData.subsections.find(s => s.slug === subsection);

  if (!subsectionData) {
    return <div>Subsection not found</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-24 pb-20">
        {/* Back Button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 font-medium"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
        </div>

        {/* Hero Section */}
        <div className="relative h-96 mb-16">
          <img
            src={sectorData.images[0]}
            alt={subsectionData.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">{subsectionData.title}</h1>
              <p className="text-xl text-gray-200 max-w-2xl">{subsectionData.shortDescription}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Overview</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-4">{subsectionData.detailedDescription}</p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Key Features</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {subsectionData.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 size={24} className="text-gray-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Benefits</h3>
                <div className="space-y-3">
                  {subsectionData.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-gray-700 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Request a Quote</h3>
                <p className="text-gray-600 mb-6">Interested in this service? Get a free consultation today.</p>
                <button
                  onClick={() => navigate('/#contact')}
                  className="w-full bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors duration-200 font-semibold"
                >
                  Contact Us
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Related Services</h3>
                <div className="space-y-3">
                  {sectorData.subsections
                    .filter(s => s.slug !== subsection)
                    .map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => navigate(`/door-automation/${s.slug}`)}
                        className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 text-gray-700 font-medium"
                      >
                        {s.title}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Gallery</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {sectorData.images.map((img, idx) => (
                <div key={idx} className="relative overflow-hidden rounded-xl shadow-lg group">
                  <img
                    src={img}
                    alt={`Gallery ${idx + 1}`}
                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DoorAutomationSubsections;